import { ChevronLeft, ChevronRight } from 'lucide-react';
import type {
  DevToolsDirectionBadgeProps,
  DevToolsDirectionChevrons,
  DevToolsDirectionLabels,
} from 'Modules/softphone/components/SoftphoneDevTools/DevToolsDirectionBadge/index.types';
import {
  DIRECTION,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import type { CallDirection } from 'Modules/softphone/types/call.types';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * What each direction says. Exhaustive by `CallDirection`, so adding a third
 * fails to compile here rather than falling through to "outbound".
 */
const DIRECTIONS = {
  inbound: { chip: DIRECTION.inbound.chip, short: 'IN', long: 'Inbound' },
  outbound: { chip: DIRECTION.outbound.chip, short: 'OUT', long: 'Outbound' },
} as const satisfies Record<CallDirection, DevToolsDirectionLabels>;

/**
 * The icons, kept apart from the labels and annotated rather than inferred:
 * they are the one thing here whose type comes from outside the repo.
 */
const CHEVRONS: DevToolsDirectionChevrons = {
  inbound: ChevronLeft,
  outbound: ChevronRight,
};

const BADGE =
  'inline-flex flex-shrink-0 items-center gap-0.5 whitespace-nowrap rounded border py-[3px] pl-1 pr-1.5 font-sp-mono text-[9.5px] font-bold uppercase tracking-[0.07em]';

/**
 * DevToolsDirectionBadge - Which way a call went
 *
 * Inbound is violet, outbound is blue, in every tab.
 */
export const DevToolsDirectionBadge: React.FC<DevToolsDirectionBadgeProps> = ({
  direction,
  compact = false,
}) => {
  const { chip, short, long } = DIRECTIONS[direction];
  const Chevron = CHEVRONS[direction];

  return (
    <span className={cn(BADGE, chip)}>
      <Chevron className="h-3 w-3 flex-shrink-0" strokeWidth={3} />
      {compact ? short : long}
    </span>
  );
};
